import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DOCUMENT, inject, Injectable, InjectionToken } from '@angular/core';
import { ENVIRONMENT, LocalStorageService } from '@iot-platform/core';
import { Environment } from '@iot-platform/models/common';
import { Amplify } from 'aws-amplify';
import { confirmResetPassword, confirmSignIn, fetchAuthSession, resetPassword, signIn, signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { catchError, defer, map, Observable, of, switchMap } from 'rxjs';
import { AuthSession, SignInResult } from '../../models/auth-session.model';
import { SsoCodeToTokensResponse, SsoRefreshTokensResponse } from '../../models/sso-code-to-tokens-response';
import { AuthProvider, authProviderRequestContext } from '../auth-provider';

/** Minimal view of an Amplify JWT. */
interface AmplifyJwt {
  payload: Record<string, unknown>;
  toString(): string;
}

interface AmplifySession {
  tokens?: { idToken?: AmplifyJwt; accessToken?: AmplifyJwt };
}

interface AmplifySignInOutput {
  isSignedIn: boolean;
  nextStep: { signInStep: string };
}

/** Amplify v6 functions used by the Cognito provider, wrapped so that specs can fake them. */
export interface AmplifyAuthApi {
  configure(config: { Auth: { Cognito: { userPoolId: string; userPoolClientId: string } } }): void;
  signIn(input: { username: string; password: string }): Promise<AmplifySignInOutput>;
  confirmSignIn(input: { challengeResponse: string }): Promise<AmplifySignInOutput>;
  signOut(): Promise<void>;
  fetchAuthSession(options?: { forceRefresh?: boolean }): Promise<AmplifySession>;
  resetPassword(input: { username: string }): Promise<unknown>;
  confirmResetPassword(input: { username: string; confirmationCode: string; newPassword: string }): Promise<void>;
  loadTokens(): Promise<{ refreshToken?: string } | null>;
}

export const AMPLIFY_AUTH_API = new InjectionToken<AmplifyAuthApi>('AMPLIFY_AUTH_API', {
  providedIn: 'root',
  factory: (): AmplifyAuthApi => ({
    configure: (config) => Amplify.configure(config),
    signIn: (input) => signIn(input),
    confirmSignIn: (input) => confirmSignIn(input),
    signOut: () => signOut(),
    fetchAuthSession: (options) => fetchAuthSession(options),
    resetPassword: (input) => resetPassword(input),
    confirmResetPassword: (input) => confirmResetPassword(input),
    loadTokens: () => cognitoUserPoolsTokenProvider.authTokenStore.loadTokens()
  })
});

/** Raw body of the Cognito hosted-UI `/oauth2/token` endpoint (`refresh_token` is absent on a refresh grant). */
interface CognitoTokenRawResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

const NO_SESSION = 'NoSession';
const NEW_PASSWORD_STEP = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

/** Amazon Cognito implementation of `AuthProvider` (aws-amplify v6). The only file importing `aws-amplify*`. */
@Injectable()
export class CognitoAuthProvider extends AuthProvider {
  private readonly amplify: AmplifyAuthApi = inject(AMPLIFY_AUTH_API);
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly storage: LocalStorageService = inject(LocalStorageService);
  private readonly document: Document = inject(DOCUMENT);

  configure(): void {
    this.amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: this.environment.cognito.userPoolId ?? '',
          userPoolClientId: this.environment.cognito.clientId ?? ''
        }
      }
    });
  }

  signIn(username: string, password: string): Observable<SignInResult> {
    return defer(() => this.doSignIn(username, password, true));
  }

  completeNewPassword(newPassword: string): Observable<AuthSession> {
    return defer(async () => {
      const output = await this.amplify.confirmSignIn({ challengeResponse: newPassword });
      if (!output.isSignedIn) {
        throw new Error(`Unsupported sign-in step: ${output.nextStep.signInStep}`);
      }
      return this.loadSession(0);
    });
  }

  getSession(options: { minValidityMs?: number } = {}): Observable<AuthSession> {
    return defer(() => this.loadSession(options.minValidityMs ?? 0));
  }

  refreshSession(): Observable<AuthSession> {
    return defer(async () => this.toSession(await this.fetchRequiredSession(true)));
  }

  isValidSession(): Observable<boolean> {
    return defer(async () => {
      const { tokens } = await this.amplify.fetchAuthSession();
      return !!tokens?.idToken && !this.expiresWithin(tokens.idToken, 0);
    }).pipe(catchError(() => of(false)));
  }

  signInWithRefreshToken(refreshToken: string, userId: string): Observable<boolean> {
    return this.httpClient
      .post<{ AuthenticationResult: { AccessToken: string; IdToken: string } }>(
        `https://cognito-idp.${this.environment.cognito.region}.amazonaws.com/`,
        {
          ClientId: this.environment.cognito.clientId,
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          AuthParameters: {
            REFRESH_TOKEN: refreshToken
          }
        },
        {
          headers: new HttpHeaders({
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
            'Content-Type': 'application/x-amz-json-1.1'
          })
        }
      )
      .pipe(
        switchMap(({ AuthenticationResult: { AccessToken, IdToken } }) => {
          this.setSharedCredentials(userId, AccessToken, refreshToken, IdToken);
          return this.isValidSession();
        }),
        catchError(() => of(false))
      );
  }

  forgotPassword(username: string): Observable<void> {
    return defer(() => this.amplify.resetPassword({ username })).pipe(map(() => undefined));
  }

  confirmForgotPassword({ username, code, password }: { username: string; code: string; password: string }): Observable<void> {
    return defer(() => this.amplify.confirmResetPassword({ username, confirmationCode: code, newPassword: password }));
  }

  signOut(): Observable<void> {
    return defer(() => this.amplify.signOut());
  }

  signInWithSso(): void {
    const sso = this.sso;
    const url = new URL(`${sso.domain}/authorize`);
    url.searchParams.set('client_id', sso.clientId);
    url.searchParams.set('idp_identifier', sso.idpIdentifier);
    url.searchParams.set('response_type', sso.responseType);
    url.searchParams.set('redirect_uri', `${window.location.origin}/${sso.redirectUri}`);
    this.document.location.href = url.toString();
  }

  exchangeSsoCode(code: string): Observable<SsoCodeToTokensResponse> {
    return this.postToken('authorization_code', 'code', code).pipe(
      map((r) => ({
        idToken: r.id_token,
        accessToken: r.access_token,
        tokenType: r.token_type,
        expiresIn: r.expires_in,
        refreshToken: r.refresh_token as string
      }))
    );
  }

  refreshSsoTokens(refreshToken: string): Observable<SsoRefreshTokensResponse> {
    return this.postToken('refresh_token', 'refresh_token', refreshToken).pipe(
      map((r) => ({
        idToken: r.id_token,
        accessToken: r.access_token,
        tokenType: r.token_type,
        expiresIn: r.expires_in
      }))
    );
  }

  private get sso(): Required<NonNullable<Environment['sso']>> {
    return this.environment.sso as Required<NonNullable<Environment['sso']>>;
  }

  private postToken(grantType: string, paramName: string, paramValue: string): Observable<CognitoTokenRawResponse> {
    const body = new URLSearchParams();
    body.set('client_id', this.sso.clientId);
    body.set('grant_type', grantType);
    body.set(paramName, paramValue);
    body.set('redirect_uri', `${window.location.origin}/login/callback`);
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.httpClient.post<CognitoTokenRawResponse>(`${this.sso.domain}/oauth2/token`, body.toString(), {
      headers,
      context: authProviderRequestContext()
    });
  }

  private async doSignIn(username: string, password: string, canRetry: boolean): Promise<SignInResult> {
    let output: AmplifySignInOutput;
    try {
      output = await this.amplify.signIn({ username, password });
    } catch (error) {
      if (canRetry && error instanceof Error && error.name === 'UserAlreadyAuthenticatedException') {
        await this.amplify.signOut();
        return this.doSignIn(username, password, false);
      }
      throw error;
    }

    const step = output.nextStep.signInStep;
    if (output.isSignedIn && step === 'DONE') {
      return { status: 'SIGNED_IN', session: await this.loadSession(0) };
    }
    if (step === NEW_PASSWORD_STEP) {
      return { status: 'NEW_PASSWORD_REQUIRED', username };
    }
    throw new Error(`Unsupported sign-in step: ${step}`);
  }

  private async fetchRequiredSession(forceRefresh: boolean): Promise<AmplifySession> {
    const session = await (forceRefresh ? this.amplify.fetchAuthSession({ forceRefresh }) : this.amplify.fetchAuthSession());
    if (!session.tokens?.idToken) {
      throw new Error(NO_SESSION);
    }
    return session;
  }

  private async loadSession(minValidityMs: number): Promise<AuthSession> {
    let session = await this.fetchRequiredSession(false);
    if (minValidityMs > 0 && session.tokens?.idToken && this.expiresWithin(session.tokens.idToken, minValidityMs)) {
      session = await this.fetchRequiredSession(true);
    }
    return this.toSession(session);
  }

  private async toSession(session: AmplifySession): Promise<AuthSession> {
    const idToken = session.tokens?.idToken;
    if (!idToken) {
      throw new Error(NO_SESSION);
    }
    const accessToken = session.tokens?.accessToken;
    const username = idToken.payload['cognito:username'] ?? accessToken?.payload.username;
    return {
      user: { username: typeof username === 'string' ? username : '' },
      tokens: {
        idToken: idToken.toString(),
        accessToken: accessToken?.toString() ?? '',
        refreshToken: await this.readRefreshToken()
      }
    };
  }

  /** Amplify v6 does not expose the refresh token in `fetchAuthSession()`: read it from the token store, then from the storage key. */
  private async readRefreshToken(): Promise<string | null> {
    try {
      const tokens = await this.amplify.loadTokens();
      if (tokens?.refreshToken) {
        return tokens.refreshToken;
      }
    } catch {
      // fall back to the storage key below
    }
    const keyPrefix = `CognitoIdentityServiceProvider.${this.environment.cognito.clientId}`;
    const lastAuthUser = this.storage.get(`${keyPrefix}.LastAuthUser`, true);
    return (lastAuthUser && this.storage.get(`${keyPrefix}.${lastAuthUser}.refreshToken`, true)) || null;
  }

  private expiresWithin(token: AmplifyJwt, validityMs: number): boolean {
    const exp = token.payload.exp;
    return typeof exp !== 'number' || exp * 1000 <= Date.now() + validityMs;
  }

  private setSharedCredentials(userId: string, accessToken: string, token: string, idToken: string): void {
    const keyPrefix = `CognitoIdentityServiceProvider.${this.environment.cognito.clientId}`;
    this.storage.set(`${keyPrefix}.${userId}.accessToken`, accessToken, true);
    this.storage.set(`${keyPrefix}.${userId}.refreshToken`, token, true);
    this.storage.set(`${keyPrefix}.${userId}.idToken`, idToken, true);
    this.storage.set(`${keyPrefix}.LastAuthUser`, userId, true);
  }
}
